<template>
  <div class="settings-page">
    <Notification
      v-model:show="showNotification"
      :message="notificationMessage"
      :type="notificationType"
    />

    <div v-if="loading" class="state-box">Chargement des paramètres…</div>
    <div v-else-if="error" class="state-box state-box--error">{{ error }}</div>

    <div v-else-if="chatbot && chatbot._id" class="settings-card">
      <!-- Panneau gauche (vert, comme l'onboarding) -->
      <aside class="sc-left">
        <div class="sc-left-overlay"></div>
        <div class="sc-left-content">
          <img :src="logoWhite" alt="Peelo" class="sc-logo" />

          <label for="avatar-upload" class="sc-avatar" title="Changer la photo">
            <img :key="avatarTimestamp" :src="avatarUrl || 'https://api.dicebear.com/9.x/bottts-neutral/svg?seed=agent'" alt="Avatar de l'agent">
            <span class="sc-avatar-edit">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
            </span>
          </label>
          <input id="avatar-upload" type="file" accept="image/jpeg,image/jpg,image/png" @change="handleAvatarUpload" hidden>

          <h2 class="sc-name">{{ chatbot.bot_name || 'Agent' }}</h2>

          <div class="sc-chips">
            <span class="sc-chip">{{ ({ whatsapp: 'WhatsApp', telegram: 'Telegram', messenger: 'Messenger', web: 'Site web' })[chatbot.type] || chatbot.type || '—' }}</span>
            <span class="sc-chip" :class="'sc-chip--' + (chatbot.status || 'active')">
              {{ ({ active: 'Actif', inactive: 'Inactif', maintenance: 'Maintenance' })[chatbot.status] || chatbot.status }}
            </span>
          </div>

          <button class="sc-id" @click="copyToClipboard(chatbot._id)" title="Copier l'identifiant">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            ID · {{ String(chatbot._id).slice(-6) }}
          </button>
        </div>
      </aside>

      <!-- Panneau droit (formulaire) -->
      <div class="sc-right">
        <div class="sc-head">
          <button class="sc-back" @click="$router.back()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            <span>Retour</span>
          </button>
          <span class="sc-eyebrow">Paramètres de l'agent</span>
          <h1 class="sc-title">Configurer votre agent</h1>
          <p class="sc-sub">Réglez le nom, le canal, le statut et la connexion.</p>
        </div>

        <div class="sc-body">
          <div class="sc-section-label">Informations générales</div>
          <div class="sc-grid">
            <div class="field">
              <label>Nom de l'agent</label>
              <input type="text" v-model="chatbot.bot_name" placeholder="Nom de l'agent">
            </div>
            <div class="field">
              <label>Canal</label>
              <div class="select-wrap">
                <select v-model="chatbot.type">
                  <option value="whatsapp">WhatsApp</option>
                  <option value="telegram">Telegram</option>
                  <option value="messenger">Messenger</option>
                  <option value="web">Site web</option>
                </select>
                <svg class="select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
            <div class="field">
              <label>Statut</label>
              <div class="select-wrap">
                <select v-model="chatbot.status">
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                  <option value="maintenance">En maintenance</option>
                </select>
                <svg class="select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
            <div class="field">
              <label>Email</label>
              <input type="email" v-model="chatbot.email" placeholder="email@exemple.com">
            </div>
            <div class="field field--full">
              <label>Description</label>
              <textarea v-model="chatbot.description" placeholder="Décrivez en quelques mots ce que fait votre agent…"></textarea>
            </div>
          </div>

          <template v-if="chatbot.type === 'whatsapp'">
            <div class="sc-section-label">Configuration WhatsApp</div>
            <div class="sc-grid">
              <div class="field">
                <label>Numéro affiché</label>
                <PhoneInput
                  v-model="chatbot.display_phone_number"
                  @country-change="handlePhoneChange"
                  placeholder="Numéro de téléphone"
                />
              </div>
              <div class="field">
                <label>ID du numéro de téléphone</label>
                <input type="text" v-model="chatbot.phone_number_id" placeholder="Phone number ID">
              </div>
              <div class="field">
                <label>ID de l'application</label>
                <input type="text" v-model="chatbot.app_id" placeholder="App ID">
              </div>
              <div class="field">
                <label>Keyword Webhook</label>
                <div class="copy-field">
                  <input type="text" v-model="chatbot.keyword_webhook" placeholder="Keyword Webhook" readonly>
                  <button class="icon-btn" @click="copyToClipboard(chatbot.keyword_webhook)" title="Copier">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  </button>
                </div>
              </div>
              <div class="field field--full">
                <label>URL Webhook</label>
                <div class="copy-field">
                  <input type="text" :value="`https://webhook.peelo.io/pipeline/whatsapp/${chatbot._id}`" placeholder="URL Webhook" readonly>
                  <button class="icon-btn" @click="copyToClipboard(`https://webhook.peelo.io/pipeline/whatsapp/${chatbot._id}`)" title="Copier">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  </button>
                </div>
              </div>
              <div class="field field--full">
                <label>Token WhatsApp</label>
                <div class="copy-field">
                  <input :type="showPassword ? 'text' : 'password'" v-model="chatbot.access_token.env.WHATSAPP_TOKEN" placeholder="Token WhatsApp">
                  <button class="icon-btn" @click="togglePasswordVisibility" title="Afficher / Masquer">
                    <svg v-if="showPassword" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="sc-actions">
          <button class="ghost-pill" @click="$router.back()">Annuler</button>
          <button class="primary-pill" @click="saveSettings">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
            <span>Enregistrer</span>
          </button>
        </div>
      </div>
    </div>

    <div v-else class="state-box">Aucune donnée de chatbot disponible</div>
  </div>
</template>

<script setup>
import logoWhite from '@/assets/logo-white.png';
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import PhoneInput from './PhoneInput.vue';
import Notification from './Notification.vue';

// Définition directe de l'URL de l'API
const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'https://api.peelo.io';

// Importation dynamique d'axios et création de l'instance
let axiosPeelo;
import('axios').then(axios => {
  axiosPeelo = axios.default.create({
    baseURL: apiBaseUrl,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Intercepteur pour ajouter le token d'authentification
  axiosPeelo.interceptors.request.use(
    (config) => {
      const userData = localStorage.getItem('user');
      if (userData) {
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Intercepteur pour gérer les réponses
  axiosPeelo.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        // Token expiré ou invalide
        localStorage.removeItem('user');
        // Rediriger vers la page de connexion si nécessaire
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
}).catch(err => {
  //console.error('Erreur lors de l\'importation d\'axios:', err);
});
const route = useRoute();
//console.log('apiBaseUrl ===>', apiBaseUrl)
//console.log('axiosPeelo ===>', axiosPeelo)
const chatbot = ref(null);
const loading = ref(false);
const error = ref(null);
const showPassword = ref(false);
const showNotification = ref(false);
const notificationMessage = ref('');
const notificationType = ref('success');
const phoneNumber = ref('');
const phoneCountry = ref(null);
const avatarTimestamp = ref(Date.now());
const avatarUrl = ref('');

const formatNumber = (number) => {
  if (number === undefined || number === null) return '0';
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const getAvatarUrl = (avatar) => {
  // Pas de photo uploadée -> avatar robot par défaut (dicebear), comme ailleurs dans l'app.
  if (!avatar || avatar === '/avatars/bot1.png') {
    const seed = (chatbot.value && (chatbot.value.bot_name || chatbot.value._id)) || 'agent';
    return `https://api.dicebear.com/9.x/bottts-neutral/svg?seed=${encodeURIComponent(seed)}`;
  }
  // URL complète (ex. dicebear déjà stocké en base) -> on la garde telle quelle.
  if (/^https?:\/\//i.test(avatar)) return avatar;
  // Sinon : fichier hébergé sur l'API (avec timestamp anti-cache).
  return `${apiBaseUrl}/public/${avatar}?v=${avatarTimestamp.value}`;
};

const updateAvatarUrl = () => {
  // Toujours définir une URL (dicebear par défaut si pas de photo).
  avatarUrl.value = getAvatarUrl(chatbot.value && chatbot.value.avatar);
};

const showNotificationMessage = (message, type = 'success') => {
  notificationMessage.value = message;
  notificationType.value = type; 
  showNotification.value = true;
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    showNotificationMessage('Copié dans le presse-papiers !', 'copy');
  }).catch(err => {
    //console.error('Erreur lors de la copie:', err);
    showNotificationMessage('Erreur lors de la copie', 'error');
  });
};

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const handleAvatarUpload = async (event) => {
  
  // Attendre que axiosPeelo soit initialisé
  if (!axiosPeelo) {
    //console.log('En attente de l\'initialisation d\'axios...');
    await new Promise(resolve => {
      const checkAxios = () => {
        if (axiosPeelo) {
          resolve();
        } else {
          setTimeout(checkAxios, 100);
        }
      };
      checkAxios();
    });
  }
  
  const file = event.target.files[0];
  
  
  if (!file) {
    
    return;
  }
  
  // Vérifier que c'est bien une image et pas un SVG
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  if (!allowedTypes.includes(file.type)) {
    
    showNotificationMessage('Format non supporté. Veuillez utiliser JPG, JPEG ou PNG');
    return;
  }
  
  try { 
    
    //loading.value = true;
    
    // Récupérer le token depuis le localStorage
    const userData = localStorage.getItem('user');
    
    
    if (!userData) {
      //console.error('Données utilisateur non trouvées');
      showNotificationMessage('Données utilisateur non trouvées');
      return;
    }
    
    // Parser l'objet JSON et extraire le token
    let token;
    try {
      const userObj = JSON.parse(userData);
      
      
      if (userObj.data && userObj.data.token) {
        token = userObj.data.token;
        
      } else {
        //console.error('Structure de données utilisateur invalide');
        showNotificationMessage('Structure de données utilisateur invalide');
        return;
      }
    } catch (parseError) {
      //console.error('Erreur lors du parsing des données utilisateur:', parseError);
      showNotificationMessage('Format de données utilisateur invalide');
      return;
    }
    
    // Créer un FormData pour envoyer le fichier
    const formData = new FormData();
    formData.append('avatar', file);
    formData.append('chatbot_id', chatbot.value._id);
   
    // Envoyer la requête pour uploader l'avatar
    
    const response = await axiosPeelo.post(
      `/upload/chatbot/avatar`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      } 
    );
    
    
    
    if (response.data && response.data.success && response.data.avatar) {
      // Mettre à jour l'avatar avec l'URL retournée par le serveur
      //console.log('Ancien avatar:', chatbot.value.avatar);
      //console.log('Nouvel avatar:', response.data.avatar);
      
      chatbot.value.avatar = response.data.avatar;
      
      
      // Mettre à jour le timestamp pour forcer le rafraîchissement de l'image
      avatarTimestamp.value = Date.now();
      updateAvatarUrl();
      
      // Afficher un message de succès
      showNotificationMessage(response.data.message || 'Avatar uploadé avec succès. Pensez à enregistrer vos modifications.', 'success');
    } else {
      //console.error('Format de réponse invalide:', response.data);
      showNotificationMessage('Erreur lors de la mise à jour de l\'avatar', 'error');
    }
  } catch (err) {
    //console.error('Erreur lors de l\'upload de l\'avatar:', err);
    //console.error('Détails de l\'erreur:', err.response ? err.response.data : 'Pas de données de réponse');
    showNotificationMessage('Erreur lors de l\'upload de l\'avatar');
  } finally {
    loading.value = false;
    
  }
};

const handlePhoneChange = (country) => {
  
};

const fetchChatbotDetails = async () => {
  
  // Attendre que axiosPeelo soit initialisé
  if (!axiosPeelo) {
    //console.log('En attente de l\'initialisation d\'axios...');
    await new Promise(resolve => {
      const checkAxios = () => {
        if (axiosPeelo) {
          resolve();
        } else {
          setTimeout(checkAxios, 100);
        }
      };
      checkAxios();
    });
  }
  
  try {
    loading.value = true;
    error.value = null;
    
    
    // Récupérer le token depuis le localStorage
    const userData = localStorage.getItem('user');
    
    
    if (!userData) {
      //console.error('Données utilisateur non trouvées');
      error.value = 'Données utilisateur non trouvées';
      return;
    }
    
    // Parser l'objet JSON et extraire le token
    let token;
    try {
      const userObj = JSON.parse(userData);
      
      
      if (userObj.data && userObj.data.token) {
        token = userObj.data.token;
        
      } else {
        //console.error('Structure de données utilisateur invalide');
        error.value = 'Structure de données utilisateur invalide';
        return;
      }
    } catch (parseError) {
      //console.error('Erreur lors du parsing des données utilisateur:', parseError);
      error.value = 'Format de données utilisateur invalide';
      return;
    }
    
    // Faire la requête pour récupérer les détails du chatbot
    
    
    const response = await axiosPeelo.get(
      `/get/strong/chatbot/${route.params.id}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    
    
    if (response.data && response.data.chatbot) {
      chatbot.value = response.data.chatbot;
      
      // Initialiser l'URL de l'avatar
      updateAvatarUrl();
      
    } else {
      //console.error('Format de réponse invalide:', response.data);
      error.value = 'Format de réponse invalide';
    }
  } catch (err) {
    //console.error('Erreur lors de la récupération des détails du chatbot:', err);
    //console.error('Détails de l\'erreur:', err.response ? err.response.data : 'Pas de données de réponse');
    error.value = err.response?.data?.error || 'Erreur lors de la récupération des détails du chatbot';
  } finally {
    loading.value = false;
    
    
  }
};

const saveSettings = async () => {
  
  // Attendre que axiosPeelo soit initialisé
  if (!axiosPeelo) {
    //console.log('En attente de l\'initialisation d\'axios...');
    await new Promise(resolve => {
      const checkAxios = () => {
        if (axiosPeelo) {
          resolve();
        } else {
          setTimeout(checkAxios, 100);
        }
      };
      checkAxios();
    });
  }
  
  try {
    //loading.value = true;
    
    // Récupérer le token depuis le localStorage
    const userData = localStorage.getItem('user');
    if (!userData) {
      error.value = 'Données utilisateur non trouvées';
      return;
    }
    
    // Parser l'objet JSON et extraire le token
    let token;
    try {
      const userObj = JSON.parse(userData);
      if (userObj.data && userObj.data.token) {
        token = userObj.data.token;
      } else {
        error.value = 'Structure de données utilisateur invalide';
        return;
      }
    } catch (parseError) {
      error.value = 'Format de données utilisateur invalide';
      return;
    }
    
    // Préparer les données à envoyer - envoyer toutes les informations du chatbot
    const chatbotData = { ...chatbot.value };
    
    
    
    // Envoyer la requête de mise à jour à l'endpoint /update/strong/chatbot
    const response = await axiosPeelo.put(
      `/update/strong/chatbot/${chatbot.value._id}`,
      chatbotData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    
    
    // Afficher un message de succès
    showNotificationMessage('Paramètres enregistrés avec succès !', 'success');
  } catch (err) {
    //console.error('Erreur lors de la sauvegarde des paramètres:', err);
    showNotificationMessage(err.response?.data?.error || 'Erreur lors de la sauvegarde des paramètres', 'error');
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchChatbotDetails();
});
</script>

<style scoped>
.settings-page {
  min-height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 28px 20px 56px;
}

.state-box {
  text-align: center;
  padding: 60px 20px;
  color: rgba(20, 48, 40, 0.6);
  font-size: 0.95rem;
}
.state-box--error { color: #E5484D; }

/* Carte 2 panneaux */
.settings-card {
  width: 100%;
  max-width: 1000px;
  display: grid;
  grid-template-columns: 0.82fr 1.18fr;
  background: #fff;
  border: 1px solid rgba(20, 48, 40, 0.08);
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 50px 110px -50px rgba(20, 48, 40, 0.5);
}

/* ---- Panneau gauche (vert + visuel) ---- */
.sc-left {
  position: relative;
  background: url('@/assets/peelochat_ecommerce.png') center / cover no-repeat;
  display: flex;
}
.sc-left-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(160deg, rgba(20, 48, 40, 0.88) 0%, rgba(20, 48, 40, 0.95) 100%);
}
.sc-left-content {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 26px;
  color: #fff;
}
.sc-logo { width: 104px; height: auto; margin-bottom: 26px; }

.sc-avatar {
  position: relative;
  width: 116px; height: 116px;
  border-radius: 30px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}
.sc-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sc-avatar-edit {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(10, 26, 20, 0.55);
  color: #fff; opacity: 0; transition: opacity 0.2s ease;
}
.sc-avatar:hover .sc-avatar-edit { opacity: 1; }

.sc-name {
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #fff;
  margin: 18px 0 14px;
  word-break: break-word;
}
.sc-chips { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; margin-bottom: 16px; }
.sc-chip {
  display: inline-flex; align-items: center;
  padding: 5px 12px; border-radius: 9999px;
  font-size: 0.76rem; font-weight: 700;
  background: rgba(218, 255, 209, 0.16); color: #DAFFD1;
}
.sc-chip--active { background: rgba(34, 197, 94, 0.22); color: #c8f7d4; }
.sc-chip--inactive { background: rgba(229, 72, 77, 0.24); color: #ffc9cb; }
.sc-chip--maintenance { background: rgba(245, 200, 92, 0.2); color: #ffe6ad; }

.sc-id {
  display: inline-flex; align-items: center; gap: 7px;
  margin-top: 4px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.78rem; font-weight: 600;
  padding: 7px 14px; border-radius: 9999px; cursor: pointer;
  transition: background 0.18s ease;
}
.sc-id:hover { background: rgba(255, 255, 255, 0.2); }

/* ---- Panneau droit (formulaire) ---- */
.sc-right {
  display: flex;
  flex-direction: column;
  padding: 32px 34px 28px;
  min-width: 0;
}
.sc-head { position: relative; margin-bottom: 22px; }
.sc-back {
  display: inline-flex; align-items: center; gap: 7px;
  background: rgba(20, 48, 40, 0.05);
  border: none; border-radius: 9999px;
  color: #143028; font-size: 0.82rem; font-weight: 700;
  padding: 8px 14px; cursor: pointer; margin-bottom: 16px;
  transition: background 0.18s ease;
}
.sc-back:hover { background: rgba(20, 48, 40, 0.12); }
.sc-eyebrow {
  display: inline-block;
  background: #DAFFD1; color: #143028;
  font-size: 10px; font-weight: 900; letter-spacing: 0.14em; text-transform: uppercase;
  padding: 5px 12px; border-radius: 9999px;
}
.sc-title { font-size: 1.55rem; font-weight: 800; letter-spacing: -0.03em; color: #143028; margin: 12px 0 4px; }
.sc-sub { font-size: 0.9rem; color: rgba(20, 48, 40, 0.6); margin: 0; }

.sc-body { flex: 1; }
.sc-section-label {
  font-size: 0.72rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
  color: rgba(20, 48, 40, 0.45);
  margin: 8px 0 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(20, 48, 40, 0.08);
}
.sc-section-label + .sc-grid { margin-bottom: 24px; }

.sc-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
.field { display: flex; flex-direction: column; gap: 7px; min-width: 0; }
.field--full { grid-column: 1 / -1; }
.field label {
  font-size: 0.74rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
  color: rgba(20, 48, 40, 0.55);
}
.field input,
.field textarea,
.field select {
  width: 100%; box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid rgba(20, 48, 40, 0.15);
  border-radius: 12px;
  font-size: 0.95rem; color: #143028; background: #fff;
  outline: none; transition: border-color 0.2s, box-shadow 0.2s;
}
.field input::placeholder,
.field textarea::placeholder { color: rgba(20, 48, 40, 0.38); }
.field input:focus,
.field textarea:focus,
.field select:focus { border-color: #143028; box-shadow: 0 0 0 3px rgba(20, 48, 40, 0.08); }
.field input[readonly] { background: #FCFAF7; color: rgba(20, 48, 40, 0.7); }
.field textarea { min-height: 96px; resize: vertical; line-height: 1.5; }

.select-wrap { position: relative; }
.select-wrap select { appearance: none; -webkit-appearance: none; padding-right: 38px; cursor: pointer; }
.select-chevron { position: absolute; right: 13px; top: 50%; transform: translateY(-50%); pointer-events: none; color: rgba(20, 48, 40, 0.5); }

.copy-field { position: relative; display: flex; }
.copy-field input { padding-right: 46px; }
.icon-btn {
  position: absolute; right: 7px; top: 50%; transform: translateY(-50%);
  background: rgba(20, 48, 40, 0.06); border: none;
  width: 32px; height: 32px; border-radius: 9999px;
  color: #143028; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.18s ease;
}
.icon-btn:hover { background: rgba(20, 48, 40, 0.14); }

.sc-actions {
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 22px; padding-top: 20px;
  border-top: 1px solid rgba(20, 48, 40, 0.08);
}
.ghost-pill {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 11px 20px; background: #fff;
  border: 1px solid rgba(20, 48, 40, 0.16); border-radius: 9999px;
  color: #143028; font-size: 0.88rem; font-weight: 700; cursor: pointer;
  transition: all 0.18s ease;
}
.ghost-pill:hover { background: rgba(20, 48, 40, 0.05); border-color: rgba(20, 48, 40, 0.3); }
.primary-pill {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 11px 22px; background: #143028; border: none; border-radius: 9999px;
  color: #fff; font-size: 0.88rem; font-weight: 800; cursor: pointer;
  transition: all 0.18s ease;
}
.primary-pill:hover { background: #1b4538; transform: translateY(-1px); }

/* ---- Phone input (vue-tel-input) ---- */
:deep(.vue-tel-input) { border: none !important; display: flex !important; align-items: center !important; }
:deep(.vti__dropdown) { padding: 0 !important; background: transparent !important; border: none !important; min-width: 88px !important; }
:deep(.vti__dropdown:hover) { background: transparent !important; }
:deep(.vti__selection) {
  padding: 11px 12px !important; background: #fff !important;
  border: 1px solid rgba(20, 48, 40, 0.15) !important; border-radius: 12px !important;
  margin-right: 8px !important; display: flex !important; align-items: center !important; gap: 8px !important;
}
:deep(.vti__selection:hover) { background: rgba(20, 48, 40, 0.04) !important; }
:deep(.vti__flag) { margin-right: 8px !important; }
:deep(.phone-input) {
  padding: 12px 14px !important; border: 1px solid rgba(20, 48, 40, 0.15) !important; border-radius: 12px !important;
  font-size: 0.95rem !important; color: #143028 !important; background: #fff !important;
  width: 100% !important; box-sizing: border-box !important; flex: 1 !important;
}
:deep(.vti__dropdown-list) {
  background: #fff !important; border: 1px solid rgba(20, 48, 40, 0.12) !important; border-radius: 12px !important;
  margin-top: 4px !important; max-height: 220px !important; overflow-y: auto !important; z-index: 5 !important;
}
:deep(.vti__dropdown-item) { padding: 8px 12px !important; display: flex !important; align-items: center !important; gap: 8px !important; cursor: pointer !important; }
:deep(.vti__dropdown-item:hover) { background: rgba(20, 48, 40, 0.04) !important; }
:deep(.vti__dropdown-item.selected) { background: #DAFFD1 !important; }

@media (max-width: 860px) {
  .settings-card { grid-template-columns: 1fr; max-width: 560px; }
  .sc-left { min-height: auto; }
  .sc-left-content { padding: 28px 24px; }
  .sc-id { margin-top: 16px; }
  .sc-right { padding: 26px 22px; }
  .sc-grid { grid-template-columns: 1fr; }
}
</style>
